import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

const apiUrl = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 10;
const SUMMARY_MAX = 2000;
const MAX_FILES = 10;
const MAX_MB = 10;
const isImageUrl = (url) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url || '');

const rowLabel = (r) => `${dayjs(r.checkIn).format('DD/MM/YYYY')}${
  r.project?.name ? ` · ${r.project.project_number ? `${r.project.project_number} · ` : ''}${r.project.name}` : ''
}`;

// modal เขียนสรุปงาน: เลือก timestamp ที่ยังไม่มีสรุป + ข้อความ + ไฟล์แนบ — ถ้าส่ง editing (แถวที่มีสรุปแล้ว) = โหมดแก้ไข
function WriteSummaryDialog({ open, onClose, onSaved, editing }) {
  const [options, setOptions] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTargetId(editing.id);
      setText(editing.summary || '');
      setFiles(editing.attachments || []);
      return;
    }
    setTargetId('');
    setText('');
    setFiles([]);
    setLoading(true);
    api
      .get(`${apiUrl}/timestamp/my-summaries`, { params: { missing: true, size: 50 } })
      .then(({ data }) => setOptions(data.rows || []))
      .finally(() => setLoading(false));
  }, [open, editing]);

  const handlePickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = picked.filter((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig.length) {
      showAlert(`ไฟล์ใหญ่เกิน ${MAX_MB} MB: ${tooBig.map((f) => f.name).join(', ')}`);
    }
    const ok = picked.filter((f) => f.size <= MAX_MB * 1024 * 1024).slice(0, MAX_FILES - files.length);
    if (!ok.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(ok.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post(`${apiUrl}/timestamp/summary-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { name: file.name, url: data.url };
      }));
      setFiles((cur) => [...cur, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`${apiUrl}/timestamp/${targetId}/summary`, { text, attachments: files });
      showAlert(editing ? 'แก้ไขสรุปงานแล้ว' : 'บันทึกสรุปงานแล้ว');
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // โหมดแก้ไขอนุญาตให้ล้างสรุปจนว่างได้ ส่วนโหมดเขียนใหม่ต้องมีข้อความหรือไฟล์อย่างน้อยหนึ่งอย่าง
  const canSave = targetId && (editing || text.trim() || files.length > 0) && !saving && !uploading;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'แก้ไขสรุปงาน' : 'เขียนสรุปงาน'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {editing ? (
            <Typography variant="body2" color="text.secondary">{rowLabel(editing)}</Typography>
          ) : (
          <TextField
            select
            fullWidth
            size="small"
            label="เลือกรายการลงเวลา (ที่ยังไม่มีสรุป)"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            disabled={loading}
            helperText={!loading && options.length === 0 ? 'ไม่มีรายการลงเวลาที่ยังไม่ได้สรุปใน 60 วันล่าสุด' : ''}
          >
            {options.map((r) => (
              <MenuItem key={r.id} value={r.id}>{rowLabel(r)}</MenuItem>
            ))}
          </TextField>
          )}

          <TextField
            multiline
            minRows={4}
            fullWidth
            label="สรุปงาน"
            placeholder="ทำอะไรบ้าง ความคืบหน้า ปัญหาที่เจอ"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, SUMMARY_MAX))}
            helperText={`${text.length}/${SUMMARY_MAX}`}
          />

          {files.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {files.map((f) => (
                <Box key={f.url} sx={{ position: 'relative' }}>
                  {isImageUrl(f.url) ? (
                    <img
                      src={f.url}
                      alt={f.name}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
                    />
                  ) : (
                    <Chip label={f.name || 'ไฟล์แนบ'} sx={{ maxWidth: 200 }} />
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setFiles((cur) => cur.filter((x) => x.url !== f.url))}
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #e5e7eb', p: 0.25, '&:hover': { bgcolor: '#fee2e2' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}

          <Box>
            <Button
              component="label"
              size="small"
              startIcon={<AttachFileIcon />}
              disabled={uploading || files.length >= MAX_FILES}
            >
              {uploading ? 'กำลังอัปโหลด...' : 'แนบรูป/ไฟล์'}
              <input hidden type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handlePickFiles} />
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? 'กำลังบันทึก...' : 'บันทึกสรุปงาน'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// การ์ด "สรุปงานของฉัน" — รายการสรุปงานรายวันที่ตัวเองเขียนไว้ (ล่าสุดก่อน) กด "ดูเพิ่ม" เพื่อโหลดหน้าถัดไป
export default function MyDailySummaries() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [writeOpen, setWriteOpen] = useState(false);
  const [editing, setEditing] = useState(null); // แถวที่กำลังแก้ไข (null = เขียนใหม่)

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    try {
      const { data } = await api.get(`${apiUrl}/timestamp/my-summaries`, {
        params: { page: nextPage, size: PAGE_SIZE },
      });
      setRows((cur) => (nextPage === 1 ? data.rows : [...cur, ...data.rows]));
      setTotal(data.total || 0);
      setPage(nextPage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <Card sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={600}>สรุปงานของฉัน</Typography>
          {total > 0 && <Chip size="small" label={`${total} รายการ`} />}
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditNoteIcon />}
          onClick={() => {
            setEditing(null);
            setWriteOpen(true);
          }}
        >
          เขียนสรุปงาน
        </Button>
      </Stack>

      {/* ล็อกความสูงไว้ที่ ~5 รายการ ที่เหลือเลื่อนดูในกรอบนี้ — เลื่อนใกล้สุดแล้วโหลดหน้าถัดไปอัตโนมัติ */}
      <Stack
        spacing={1.5}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (!loading && rows.length < total && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
            load(page + 1);
          }
        }}
        sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}
      >
        {rows.map((r) => (
          <Box key={r.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {rowLabel(r)}
              </Typography>
              <Button
                size="small"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={() => {
                  setEditing(r);
                  setWriteOpen(true);
                }}
              >
                แก้ไข
              </Button>
            </Stack>
            {r.summary && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {r.summary}
              </Typography>
            )}
            {r.attachments.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {r.attachments.map((f) => (isImageUrl(f.url) ? (
                  <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
                    <img
                      src={f.url}
                      alt={f.name}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
                    />
                  </a>
                ) : (
                  <Chip key={f.url} component="a" href={f.url} target="_blank" clickable size="small" label={f.name || 'ไฟล์แนบ'} sx={{ maxWidth: 200 }} />
                )))}
              </Stack>
            )}
          </Box>
        ))}

        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            ยังไม่มีสรุปงาน — กด "เขียนสรุปงาน" เพื่อเริ่มต้น
          </Typography>
        )}
        {loading && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            กำลังโหลด...
          </Typography>
        )}
        {!loading && rows.length < total && (
          <Button onClick={() => load(page + 1)}>ดูเพิ่ม</Button>
        )}
      </Stack>

      <WriteSummaryDialog
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onSaved={() => load(1)}
        editing={editing}
      />
    </Card>
  );
}
